import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Ticket, Download, Calendar, MapPin, X } from "lucide-react";
import QRCode from "qrcode";
import { useAuth } from "../../context/AuthContext";

const statusColor = {
  paid: "bg-green-100 text-green-700",
  used: "bg-slate-200 text-slate-600",
  refunded: "bg-red-100 text-red-600",
  cancelled: "bg-amber-100 text-amber-700",
};

const EMPTY_FILTERS = { event: "", order: "", validOn: "", status: "", text: "", showAll: false };
const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => { try { return new Date(`${d}T12:00:00`).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return d; } };
const lastDay = (t) => [...(t.valid_dates || []), t.event_end, t.event_start].filter(Boolean).map(d => String(d).slice(0, 10)).sort().pop();
const isUpcomingValid = (t) => t.status === "paid" && (!lastDay(t) || lastDay(t) >= todayIso());

const applyTicketFilters = (tickets, f) => {
  const text = f.text.trim().toLowerCase();
  const order = f.order.trim().toLowerCase();
  return tickets.filter(t =>
    (f.showAll || isUpcomingValid(t)) &&
    (!f.event || t.event_id === f.event) &&
    (!order || (t.order_id || "").toLowerCase().includes(order)) &&
    (!f.validOn || (t.valid_dates || []).includes(f.validOn)) &&
    (!f.status || t.status === f.status) &&
    (!text || [t.tier_name, t.variant_name, t.id, t.attendee_name, t.event_name].filter(Boolean).some(v => v.toLowerCase().includes(text)))
  );
};

const fCls = "px-3 py-2 rounded-full border text-sm bg-white";

function TicketFilters({ tickets, filters, setFilters, shown }) {
  const set = (patch) => setFilters(f => ({ ...f, ...patch }));
  const events = [...new Map(tickets.map(t => [t.event_id, t.event_name || "Event"])).entries()];
  const dates = [...new Set(tickets.flatMap(t => t.valid_dates || []))].sort();
  const active = Object.entries(filters).some(([k, v]) => k !== "showAll" && v);
  return (
    <div className="mb-4 space-y-2" data-testid="my-tickets-filters">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        <select value={filters.event} onChange={e => set({ event: e.target.value })} className={fCls} data-testid="my-tickets-filter-event">
          <option value="">All events</option>
          {events.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <select value={filters.validOn} onChange={e => set({ validOn: e.target.value })} className={fCls} data-testid="my-tickets-filter-valid-on">
          <option value="">Valid on: any day</option>
          {dates.map(d => <option key={d} value={d}>{fmtDate(d)}</option>)}
        </select>
        <select value={filters.status} onChange={e => set({ status: e.target.value, showAll: e.target.value && e.target.value !== "paid" ? true : filters.showAll })} className={fCls} data-testid="my-tickets-filter-status">
          <option value="">All statuses</option>
          <option value="paid">Valid</option><option value="used">Used</option>
          <option value="cancelled">Cancelled</option><option value="refunded">Refunded</option>
        </select>
        <input value={filters.order} onChange={e => set({ order: e.target.value })} placeholder="Order ID" className={fCls} data-testid="my-tickets-filter-order" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input value={filters.text} onChange={e => set({ text: e.target.value })} placeholder="Search tier, attendee, ticket ID…" className={`${fCls} flex-1 min-w-[180px]`} data-testid="my-tickets-search-input" />
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" data-testid="my-tickets-show-all-label">
          <input type="checkbox" checked={filters.showAll} onChange={e => set({ showAll: e.target.checked })} data-testid="my-tickets-show-all" />
          Show past, used &amp; cancelled
        </label>
        {active && <button onClick={() => setFilters(f => ({ ...EMPTY_FILTERS, showAll: f.showAll }))} className="text-xs text-[#1e3a5f] underline" data-testid="my-tickets-clear-filters">Clear filters</button>}
        <span className="text-xs text-slate-400 ml-auto" data-testid="my-tickets-count">{shown} of {tickets.length}</span>
      </div>
    </div>
  );
}

/**
 * Reusable inner list of the current user's tickets.
 * Used standalone via MyTickets page and inline as a tab inside WalletPortal.
 */
export function MyTicketsList({ tenantSlug: tenantSlugProp }) {
  const [tickets, setTickets] = useState([]);
  const [qrMap, setQrMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const tenantSlug = tenantSlugProp || params.tenantSlug || (window.location.pathname.split("/")[1] || "");

  const load = async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`/ticketing/my-tickets`);
      setTickets(r.data || []);
      const qrs = {};
      for (const t of r.data || []) {
        qrs[t.id] = await QRCode.toDataURL(t.qr_code, { width: 200, margin: 1 });
      }
      setQrMap(qrs);
    } catch { setTickets([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const shown = applyTicketFilters(tickets, filters);

  const downloadPdf = async (ticketId) => {
    try {
      const res = await authAxios.get(`/ticketing/my-tickets/${ticketId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ticket-${ticketId.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const cancelTicket = async (ticketId) => {
    try {
      const r = await authAxios.post(`/ticketing/my-tickets/${ticketId}/cancel`);
      toast.success(r.data.message);
      setConfirmCancel(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancellation failed"); }
  };

  return (
    <div data-testid="my-tickets-inline">
      {!loading && tickets.length > 0 && (
        <TicketFilters tickets={tickets} filters={filters} setFilters={setFilters} shown={shown.length} />
      )}
      {loading && <p className="text-slate-400 text-center py-8">Loading...</p>}
      {!loading && tickets.length > 0 && shown.length === 0 && (
        <p className="text-center text-sm text-slate-400 py-10" data-testid="my-tickets-no-match">No tickets match these filters.</p>
      )}
      {!loading && tickets.length === 0 && (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
          <Ticket className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">No tickets yet</p>
          <button onClick={() => navigate(`/${tenantSlug}`)} className="mt-4 px-5 py-2 rounded-full bg-[#1e3a5f] text-white text-sm font-medium" data-testid="browse-events-btn">Browse Events</button>
        </div>
      )}
      <div className="space-y-4">
        {shown.map(t => (
          <div key={t.id} className="bg-white rounded-2xl overflow-hidden shadow-sm" data-testid={`my-ticket-${t.id}`}>
            <div className="p-5 flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-bold text-[#1e3a5f]">{t.event_name}</h3>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusColor[t.status] || "bg-slate-100 text-slate-500"}`}>{t.status}</span>
                </div>
                <p className="text-sm text-slate-600 mb-2">{t.tier_name}{t.variant_name ? ` · ${t.variant_name}` : ""}</p>
                {t.attendee_name && t.attendee_name !== t.buyer_name && (
                  <p className="text-xs text-[#0ea5e9] font-semibold mb-2" data-testid={`ticket-attendee-${t.id}`}>
                    Attendee: {t.attendee_name}
                    {t.attendee_member_id && (
                      <span className="ml-2 text-[10px] bg-cyan-100 text-cyan-700 rounded-full px-1.5 py-0.5 align-middle" data-testid={`ticket-attendee-account-${t.id}`}>Registered account</span>
                    )}
                  </p>
                )}
                {t.is_attendee_view && (
                  <p className="text-xs text-slate-500 mb-2" data-testid={`ticket-purchased-by-${t.id}`}>
                    Purchased by <span className="font-semibold text-slate-700">{t.buyer_name}</span> for you
                  </p>
                )}
                <div className="flex flex-col gap-1 text-xs text-slate-500">
                  {t.event_start && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {t.event_start}</span>}
                  {t.event_location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.event_location}</span>}
                </div>
                {(t.valid_dates || []).length > 0 && (
                  <p className="mt-1 text-xs text-slate-600" data-testid={`my-ticket-valid-on-${t.id}`}>Valid on: {t.valid_dates.map(fmtDate).join(", ")}</p>
                )}
                <p className="mt-2 text-xs text-slate-400">Paid €{t.amount_paid.toFixed(2)} · ID {t.id.slice(0, 8)}{t.order_id ? ` · Order #${t.order_id.slice(0, 8)}` : ""}</p>
                <div className="mt-3 flex gap-3 flex-wrap">
                  {t.status === "paid" && (
                    <button onClick={() => downloadPdf(t.id)} className="flex items-center gap-1 text-[#1e3a5f] text-sm font-semibold hover:underline" data-testid={`download-ticket-${t.id}`}>
                      <Download className="w-4 h-4" /> Download PDF
                    </button>
                  )}
                  {t.cancellable && (
                    <button onClick={() => setConfirmCancel(t)} className="flex items-center gap-1 text-red-500 text-sm font-semibold hover:underline" data-testid={`cancel-ticket-${t.id}`}>
                      <X className="w-4 h-4" /> Cancel
                    </button>
                  )}
                </div>
                {t.status === "cancelled" && (
                  <p className="mt-2 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                    Refund €{(t.refund_amount || 0).toFixed(2)} via {t.refund_method} {t.refund_ref && t.refund_ref !== "wallet" ? `(code: ${t.refund_ref})` : ""}
                  </p>
                )}
              </div>
              {qrMap[t.id] && t.status === "paid" && (
                <div className="flex flex-col items-center">
                  <img src={qrMap[t.id]} alt="QR" className="w-32 h-32" />
                  <p className="text-[10px] text-slate-400 mt-1">Scan at gate</p>
                  <p
                    className="text-[10px] font-mono text-slate-500 mt-0.5 select-all"
                    title={t.id}
                    data-testid={`my-ticket-shortid-${t.id}`}
                  >
                    Ticket #{(t.id || "").slice(0, 8)}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {confirmCancel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setConfirmCancel(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-3xl p-6 max-w-md w-full">
            <h3 className="text-lg font-extrabold text-[#1e3a5f] mb-2">Cancel ticket?</h3>
            <p className="text-sm text-slate-600 mb-4">Cancelling "{confirmCancel.tier_name}" — your refund will follow the shop's cancellation policy. Continue?</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmCancel(null)} className="flex-1 py-3 rounded-full bg-slate-100 text-slate-600 font-medium" data-testid="cancel-cancel-btn">Keep ticket</button>
              <button onClick={() => cancelTicket(confirmCancel.id)} className="flex-1 py-3 rounded-full bg-red-500 text-white font-bold" data-testid="confirm-cancel-btn">Cancel ticket</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MyTickets() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="my-tickets">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`/${tenantSlug}/dashboard`)} className="p-2 hover:bg-slate-100 rounded-full"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
          <h1 className="font-bold text-[#1e3a5f]">My Tickets</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4">
        <MyTicketsList tenantSlug={tenantSlug} />
      </main>
    </div>
  );
}
